import JSZip from 'jszip'

export interface ApkVersionInfo {
  versionCode: number | null
  versionName: string | null
}

/**
 * 从 APK 文件中解析版本信息
 */
export async function parseApkVersion(file: File): Promise<ApkVersionInfo> {
  const zip = await JSZip.loadAsync(file)
  const manifestEntry = zip.file('AndroidManifest.xml')
  if (!manifestEntry) {
    throw new Error('APK 中未找到 AndroidManifest.xml')
  }

  const buffer = await manifestEntry.async('arraybuffer')
  return parseBinaryManifest(buffer)
}

/**
 * 解析 Android 二进制 XML 格式的 AndroidManifest.xml
 * 提取 versionCode 和 versionName
 */
function parseBinaryManifest(buffer: ArrayBuffer): ApkVersionInfo {
  const data = new DataView(buffer)
  const uint8 = new Uint8Array(buffer)
  let offset = 0

  // --- File Header ---
  offset = 8

  // --- String Pool Chunk (type 0x001C0001) ---
  const spType = data.getUint32(offset, true)
  if ((spType & 0xFFFF) !== 0x0001) {
    throw new Error('Invalid string pool chunk')
  }
  const spSize = data.getUint32(offset + 4, true)
  const stringCount = data.getUint32(offset + 8, true)
  const flags = data.getUint32(offset + 16, true)
  const stringsStart = data.getUint32(offset + 20, true)

  const isUTF8 = (flags & (1 << 8)) !== 0

  // Read string offsets
  const stringOffsets: number[] = []
  let pos = offset + 28
  for (let i = 0; i < stringCount; i++) {
    stringOffsets.push(data.getUint32(pos, true))
    pos += 4
  }

  // Read strings
  const strings: string[] = []
  const strDataStart = offset + stringsStart
  for (let i = 0; i < stringCount; i++) {
    const strOffset = strDataStart + stringOffsets[i]
    try {
      if (isUTF8) {
        strings.push(readUTF8String(data, uint8, strOffset))
      } else {
        strings.push(readUTF16String(data, strOffset))
      }
    } catch {
      strings.push('')
    }
  }

  offset += spSize

  // --- Resource ID Table (type 0x00080180) ---
  const resourceIds: number[] = []
  if (offset < buffer.byteLength) {
    const chunkType = data.getUint32(offset, true)
    if ((chunkType & 0xFFFF) === 0x0180) {
      const chunkSize = data.getUint32(offset + 4, true)
      const count = (chunkSize - 8) / 4
      for (let i = 0; i < count; i++) {
        resourceIds.push(data.getUint32(offset + 8 + i * 4, true))
      }
      offset += chunkSize
    }
  }

  // --- Scan XML chunks for <manifest> start tag ---
  let versionCode: number | null = null
  let versionName: string | null = null

  const RES_VERSION_CODE = 0x0101021b
  const RES_VERSION_NAME = 0x0101021c

  while (offset < buffer.byteLength - 8) {
    const chunkType = data.getUint16(offset, true)
    const chunkSize = data.getUint32(offset + 4, true)

    if (chunkSize <= 0 || chunkSize > buffer.byteLength - offset) break

    // START_TAG = 0x0102
    if (chunkType === 0x0102) {
      const nameIdx = data.getInt32(offset + 20, true)
      const tagName = getString(strings, nameIdx)

      if (tagName === 'manifest') {
        const attrCount = data.getUint16(offset + 28, true)
        const attrSize = data.getUint16(offset + 26, true) || 20
        const attrStartOffset = data.getUint16(offset + 24, true)
        const attrsBase = offset + 16 + attrStartOffset

        for (let i = 0; i < attrCount; i++) {
          const ao = attrsBase + i * attrSize
          if (ao + 20 > buffer.byteLength) break

          const attrNameIdx = data.getInt32(ao + 4, true)
          const rawValueIdx = data.getInt32(ao + 8, true)
          const valueType = data.getUint8(ao + 15)
          const valueData = data.getInt32(ao + 16, true)

          const resId = (attrNameIdx >= 0 && attrNameIdx < resourceIds.length)
            ? resourceIds[attrNameIdx]
            : 0

          const attrName = getString(strings, attrNameIdx)

          if (resId === RES_VERSION_CODE || attrName === 'versionCode') {
            versionCode = valueData
          } else if (resId === RES_VERSION_NAME || attrName === 'versionName') {
            if (valueType === 0x03) {
              versionName = getString(strings, valueData)
            } else if (rawValueIdx >= 0) {
              versionName = getString(strings, rawValueIdx)
            }
          }
        }
        break
      }
    }

    offset += chunkSize
  }

  return { versionCode, versionName }
}

function getString(strings: string[], index: number): string {
  return (index >= 0 && index < strings.length) ? strings[index] : ''
}

function readUTF8String(data: DataView, uint8: Uint8Array, offset: number): string {
  let p = offset
  let charLen = data.getUint8(p++)
  if (charLen & 0x80) {
    charLen = ((charLen & 0x7f) << 8) | data.getUint8(p++)
  }
  let byteLen = data.getUint8(p++)
  if (byteLen & 0x80) {
    byteLen = ((byteLen & 0x7f) << 8) | data.getUint8(p++)
  }
  return new TextDecoder('utf-8').decode(uint8.slice(p, p + byteLen))
}

function readUTF16String(data: DataView, offset: number): string {
  let p = offset
  let charCount = data.getUint16(p, true)
  p += 2
  if (charCount & 0x8000) {
    charCount = ((charCount & 0x7FFF) << 16) | data.getUint16(p, true)
    p += 2
  }
  let str = ''
  for (let j = 0; j < charCount; j++) {
    str += String.fromCharCode(data.getUint16(p, true))
    p += 2
  }
  return str
}
